import React, { useMemo } from 'react';
import { Button, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconSquareRoundedX } from '@tabler/icons-react';

import { useAuthStore } from '../../stores/auth.store';
import { PriceChangeRequestAction } from '../../codegen/graphql';
import { useRequestDrawer } from '../../context/RequestDrawerContext';

type Props = {
    openPriceChangeRequestModal: (verb: string) => void;
};

function RequestDrawerButtons({ openPriceChangeRequestModal }: Props) {
    const { userId, standInForUserIds } = useAuthStore();
    const { actions: contextActions, hideDrawerActions } = useRequestDrawer();

    const allResponsibleUserIds = useMemo(
        () => [userId, ...standInForUserIds],
        [userId, standInForUserIds],
    );

    const actions: PriceChangeRequestAction[] = contextActions;

    const myAction = useMemo(() => {
        return actions.find(
            (action: PriceChangeRequestAction) =>
                allResponsibleUserIds.includes(action.responsibleUser?.id) &&
                !action?.completed,
        );
    }, [actions, allResponsibleUserIds]);

    const activeAction = myAction;
    const isSeniorOpsExecutiveApproval =
        activeAction?.action === 'SeniorOpsExecutiveEscalationApproval' ||
        activeAction?.action === 'SeniorOpsFreezeSecondaryEscalation';
    const isProcurementApproval = useMemo(() => {
        return (
            activeAction?.action !== 'DivisionManagerApproval' &&
            activeAction?.action !== 'OpsExecutiveEscalationApproval' &&
            activeAction?.action !== 'OpsExecutivePriceChangeFreezeApproval' &&
            activeAction?.action !== 'SeniorOpsExecutiveEscalationApproval' &&
            activeAction?.action !== 'SeniorOpsFreezeSecondaryEscalation'
        );
    }, [activeAction]);

    const priceChangeCompleted = !myAction || myAction?.completed;

    if (hideDrawerActions) {
        return null;
    }

    if (
        !myAction ||
        priceChangeCompleted ||
        isSeniorOpsExecutiveApproval ||
        !allResponsibleUserIds.includes(myAction?.responsibleUser?.id)
    ) {
        return null;
    }

    const isSupplierEngagement = myAction?.action === 'SupplierEngagement';

    if (isSupplierEngagement) {
        return (
            <div className="flex gap-5 justify-end">
                <Tooltip
                    label="The supplier approved new price."
                    openDelay={500}
                    zIndex={2000}
                >
                    <Button
                        leftSection={<IconCircleCheck />}
                        radius={6}
                        variant="light"
                        size="md"
                        color="green"
                        fullWidth={false}
                        onClick={() =>
                            openPriceChangeRequestModal('accept-supplier')
                        }
                    >
                        Supplier Approved
                    </Button>
                </Tooltip>
                <Tooltip
                    label="The supplier declined new price."
                    openDelay={500}
                    zIndex={2000}
                >
                    <Button
                        leftSection={<IconSquareRoundedX />}
                        radius={6}
                        color="red"
                        variant="light"
                        size="md"
                        fullWidth={false}
                        onClick={() =>
                            openPriceChangeRequestModal('decline-supplier')
                        }
                    >
                        Supplier Declined
                    </Button>
                </Tooltip>
            </div>
        );
    }

    return (
        <div className="flex gap-5 justify-end">
            {isProcurementApproval && (
                <Tooltip
                    label="Suggest a intrim price to engage with supplier"
                    openDelay={500}
                    zIndex={2000}
                >
                    <Button
                        variant="outline"
                        className=""
                        classNames={{
                            root: 'border-primary/20',
                        }}
                        radius={6}
                        size="md"
                        fullWidth={false}
                        onClick={() => openPriceChangeRequestModal('suggest')}
                    >
                        Suggest Interim Price
                    </Button>
                </Tooltip>
            )}
            <Tooltip
                label="Accept the price change request"
                openDelay={500}
                zIndex={2000}
            >
                <Button
                    leftSection={<IconCircleCheck />}
                    radius={6}
                    variant="light"
                    size="md"
                    color="green"
                    fullWidth={false}
                    onClick={() => openPriceChangeRequestModal('accept')}
                >
                    Accept
                </Button>
            </Tooltip>
            <Tooltip
                label="Decline the price change request"
                openDelay={500}
                zIndex={2000}
            >
                <Button
                    leftSection={<IconSquareRoundedX />}
                    radius={6}
                    color="red"
                    variant="light"
                    size="md"
                    fullWidth={false}
                    onClick={() => openPriceChangeRequestModal('decline')}
                >
                    Decline
                </Button>
            </Tooltip>
        </div>
    );
}
export default RequestDrawerButtons;
